// Release-notes markdown → React elements (no HTML injection). Supports what changelogs use:
// #/##/### headings, - or * bullets, 1. numbered lists, paragraphs, **bold**, *italic*, `code`,
// and [links](https://…) (http/https only).

import { Fragment, type ReactNode } from "react";

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith("**")) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("[")) {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok);
      const href = lm?.[2] ?? "";
      if (lm && /^https?:\/\//i.test(href)) {
        out.push(
          <a key={key} href={href} target="_blank" rel="noreferrer noopener">
            {lm[1]}
          </a>,
        );
      } else out.push(lm?.[1] ?? tok);
    } else out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block = { kind: "h"; level: number; text: string } | { kind: "p"; text: string } | { kind: "ul" | "ol"; items: string[] };

function blocks(src: string): Block[] {
  const out: Block[] = [];
  let para: string[] = [];
  let list: { kind: "ul" | "ol"; items: string[] } | null = null;
  const flush = () => {
    if (para.length) out.push({ kind: "p", text: para.join(" ") });
    para = [];
    if (list) out.push(list);
    list = null;
  };
  for (const raw of src.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    const ul = /^[-*•]\s+(.*)$/.exec(line);
    const ol = /^\d+[.)]\s+(.*)$/.exec(line);
    if (h) {
      flush();
      out.push({ kind: "h", level: h[1].length, text: h[2] });
    } else if (ul || ol) {
      const kind = ul ? "ul" : "ol";
      if (para.length) {
        out.push({ kind: "p", text: para.join(" ") });
        para = [];
      }
      if (!list || list.kind !== kind) {
        if (list) out.push(list);
        list = { kind, items: [] };
      }
      list.items.push((ul ?? ol)![1]);
    } else {
      if (list) {
        out.push(list);
        list = null;
      }
      para.push(line);
    }
  }
  flush();
  return out;
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const bs = blocks(source);
  if (!bs.length) return null;
  return (
    <div className={className}>
      {bs.map((b, i) => {
        const k = `b${i}`;
        if (b.kind === "h") {
          const Tag = b.level <= 2 ? "h4" : "h5";
          return <Tag key={k}>{inline(b.text, k)}</Tag>;
        }
        if (b.kind === "p") return <p key={k}>{inline(b.text, k)}</p>;
        const List = b.kind;
        return (
          <List key={k}>
            {b.items.map((it, j) => (
              <li key={`${k}-${j}`}>
                <Fragment>{inline(it, `${k}-${j}`)}</Fragment>
              </li>
            ))}
          </List>
        );
      })}
    </div>
  );
}
